// auth-helpers.ts
// crypto será usado apenas no servidor, removido para compatibilidade com browser

import jwt_decode from "jwt-decode";
import { Dispatch, SetStateAction } from "react";
import { NavigateFunction } from "react-router-dom";

import LOCAL_STORAGE from "@/constants/local-storage";
import ROUTE_URLS from "@/constants/route-urls";
import { InvalidTokenError, UserNotFound } from "@/errors/auth.errors";
import { IDecodedToken } from "@/interfaces/auth.interfaces";
import { ProfileCode } from "@/interfaces/permission.interfaces";
import { PaymentPayload } from "@/interfaces/talk2pay.iterfaces";
import IUser, { IUserProfile as IUserUserProfile } from "@/interfaces/user.interface";
import { userService } from "@/services/user.service";
import { UserStatus } from "@/types/user.types";

export const getAccessToken = (): string | null => {
  return localStorage.getItem(LOCAL_STORAGE.accessTokenKey);
};

export const getCustomerId = (): string | null => {
  const accessToken = getAccessToken();
  if (!accessToken) return null;

  try {
    const decoded: IDecodedToken = jwt_decode(accessToken);
    return decoded.CustomerId || null;
  } catch (error) {
    return null;
  }
};

export const generateDecodedToken = (
  _accessToken: string,
  setIsEmailConfirmed?: Dispatch<SetStateAction<boolean>>
): IDecodedToken | null => {
  const decoded: IDecodedToken = jwt_decode(_accessToken);
  if (!decoded) return null;
  if (setIsEmailConfirmed !== undefined) {
    if (isImpersonationSession(decoded)) {
      setIsEmailConfirmed(true);
    } else {
      setIsEmailConfirmed(decoded.ConfirmedEmail?.toLocaleUpperCase() === "TRUE");
    }
  }
  return decoded;
};

export const checkIfTokenIsExpired = (decodedToken: IDecodedToken): boolean => {
  const expiration = decodedToken.exp * 1000;
  const currentDate = new Date().getTime();
  const isExpired = currentDate > expiration;
  return isExpired;
};

export function decodeToken(
  accessToken: string,
  setIsEmailConfirmed?: Dispatch<SetStateAction<boolean>>
): IDecodedToken {
  const decoded = generateDecodedToken(accessToken, setIsEmailConfirmed);
  if (!decoded) throw new InvalidTokenError();
  return decoded;
}

export async function fetchUser(userId: number): Promise<IUser> {
  const user = await userService.getById(userId);
  if (!user) throw new UserNotFound();

  // Converter campos do service para a interface IUser
  const userWithCorrectFields: IUser = {
    ...user,
    status: user.status as UserStatus,
    languageCode: user.languageCode as "PT-BR",
    rowDate: user.rowdate ? new Date(user.rowdate) : new Date(),
    lastAccess: user.lastAccess ? new Date(user.lastAccess) : new Date(),
    permissions:
      user.permissions?.map((permission) => ({
        ...permission,
        rowdate: permission.rowdate ? new Date(permission.rowdate) : new Date(),
      })) || [],
    // Mapear userProfile para o formato esperado pelo Front (IUserProfile estende IProfile + profileId)
    userProfile: (user.userProfile?.map((profile) => ({
      profileId: profile.profileId,
      code: ProfileCode.ATTENDANT,
      label: "",
      rowdate: new Date(profile.rowDate),
      profileModule: [],
    })) || []) as unknown as IUserUserProfile[],
  };

  return userWithCorrectFields;
}

export function handleUserRedirects(
  router: NavigateFunction,
  lang: "PT-BR",
  isEmailConfirmed: boolean,
  urlToRedirect: string,
  isPasswordExpired: boolean = false
) {
  // PRIORIDADE 1: Verificar senha expirada PRIMEIRO
  if (isPasswordExpired) {
    router(ROUTE_URLS.changePasswordExpired(lang, true), { replace: true });
    return;
  }

  // PRIORIDADE 2: Verificar email confirmado
  if (!isEmailConfirmed) {
    router(ROUTE_URLS.emailValidation(lang), { replace: true });
    return;
  }

  // PRIORIDADE 3: Redirect padrão
  router(urlToRedirect, { replace: true });
}

export function performUnauthenticatedRedirect(
  redirectUrlLogout: string,
  router: NavigateFunction,
  pathname: string,
  lang: "PT-BR"
) {
  if (redirectUrlLogout !== "") {
    router(redirectUrlLogout, { replace: true });
  } else if (pathname.includes("emailConfirmation")) {
    router(ROUTE_URLS.serviceDashboard(lang), { replace: true });
  } else {
    router(ROUTE_URLS.loginRedirect(pathname, lang), { replace: true });
  }
}

export const getClientIp = async (): Promise<string | null> => {
  try {
    const response = await fetch("https://api.ipify.org?format=json");

    const data = await response.json();

    return data.ip;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Erro ao obter o IP do cliente:", error);
    return null;
  }
};

export const fetchClientIp = async () => {
  try {
    return await getClientIp();
  } catch (error) {
    return "";
  }
};

export async function generatePaymentHash(
  timestamp: number,
  data: PaymentPayload,
  customerId: number
): Promise<string> {
  const hashKey = import.meta.env.VITE_HASH_KEY;
  if (!hashKey) {
    throw new Error("VITE_HASH_KEY não está configurada no ambiente.");
  }

  const {
    platformId,
    beneficiaryAccountId,
    operationType,
    pixAddressKey,
    pixAddressKeyType,
    description,
    scheduleDate,
    amount,
  } = data;

  const rawData = `${hashKey}${timestamp}${customerId}${platformId}${beneficiaryAccountId}${operationType}${pixAddressKey}${pixAddressKeyType}${description}${scheduleDate}${Number(amount).toFixed(2)}`;

  // Usando Web Crypto API para compatibilidade com browser
  const encoder = new TextEncoder();
  const data_encoded = encoder.encode(rawData);
  const hash_buffer = await crypto.subtle.digest("SHA-256", data_encoded);
  const hash_array = Array.from(new Uint8Array(hash_buffer));
  const hash = hash_array.map((b) => b.toString(16).padStart(2, "0")).join("");

  return hash;
}

export function validateImpersonationToken(decoded: IDecodedToken): void {
  if (decoded.login_mode !== "backoffice_impersonation") {
    throw new Error("Token inválido");
  }
  if (!decoded.impersonation_session_id) {
    throw new Error("Token de impersonation inválido");
  }
}

export function isImpersonationSession(decoded: IDecodedToken): boolean {
  return decoded.login_mode === "backoffice_impersonation";
}
